import type { Field } from "@puckeditor/core";

import type { ExperienceComponent } from "./manifest";

export type ExperienceComponentType = ExperienceComponent["component"];
export type ExperienceDataKind = "scalar" | "record" | "collection" | "document" | "action";

export interface ExperienceComponentDefinition {
  readonly type: ExperienceComponentType;
  readonly version: 1;
  readonly label: string;
  readonly description: string;
  readonly category: "Insight" | "Dati" | "Documenti" | "Azioni";
  readonly accepts: readonly ExperienceDataKind[];
  readonly defaultSpan: ExperienceComponent["span"];
  /** Puck-native defaults. They are persisted in the manifest when a block is inserted. */
  readonly defaultProps: Readonly<Record<string, string | number | boolean | null>>;
  /** Managed bindings are derived from the flow and intentionally cannot be changed in the UI. */
  readonly bindingPolicy: "required" | "managed" | "none";
  readonly bindingLabel?: string;
  /** Puck-native authoring schema. Field names map 1:1 to manifest properties. */
  readonly fields: Readonly<Record<string, Field>>;
}

const definitions: readonly ExperienceComponentDefinition[] = [
  {
    type: "commission-summary",
    version: 1,
    label: "Riepilogo provvigioni",
    description: "Indicatori economici e controlli del periodo.",
    category: "Insight",
    accepts: ["collection"],
    defaultSpan: { desktop: 12, tablet: 8, mobile: 4 },
    defaultProps: {
      sectionTitle: "Riepilogo ordini e provvigioni",
      invoiceCountLabel: "Fatture / righe",
      revenueLabel: "Totale fatturato",
      transportLabel: "di cui trasporto",
      commissionBaseLabel: "Imponibile provvigioni",
      payableLabel: "Provvigioni da pagare",
      secondaryLabel: "Provvigione Strada",
      reviewLabel: "Dati da verificare",
      invoiceIdField: "invoice_number",
      revenueField: "net_line_amount",
      transportField: "shipping_offered",
      commissionBaseField: "commission_base",
      payableField: "commission_amount",
      secondaryField: "secondary_commission_amount",
      reviewField: "cost_unit_price",
    },
    bindingPolicy: "managed",
    bindingLabel: "Righe provvigionali",
    fields: {
      sectionTitle: { type: "text", label: "Titolo riepilogo" },
      invoiceCountLabel: { type: "text", label: "Etichetta fatture" },
      revenueLabel: { type: "text", label: "Etichetta fatturato" },
      transportLabel: { type: "text", label: "Etichetta trasporto" },
      commissionBaseLabel: { type: "text", label: "Etichetta imponibile" },
      payableLabel: { type: "text", label: "Etichetta da pagare" },
      secondaryLabel: { type: "text", label: "Etichetta provvigione secondaria" },
      reviewLabel: { type: "text", label: "Etichetta verifiche" },
      invoiceIdField: {
        type: "text",
        label: "Campo identificativo fattura",
        placeholder: "invoice_number",
      },
      revenueField: {
        type: "text",
        label: "Campo da sommare · fatturato",
        placeholder: "net_line_amount",
      },
      transportField: {
        type: "text",
        label: "Campo da sommare · trasporto",
        placeholder: "shipping_offered",
      },
      commissionBaseField: {
        type: "text",
        label: "Campo da sommare · imponibile",
        placeholder: "commission_base",
      },
      payableField: {
        type: "text",
        label: "Campo da sommare · provvigioni",
        placeholder: "commission_amount",
      },
      secondaryField: {
        type: "text",
        label: "Campo da sommare · provvigione secondaria",
        placeholder: "secondary_commission_amount",
      },
      reviewField: {
        type: "text",
        label: "Campo da controllare · dati mancanti",
        placeholder: "cost_unit_price",
      },
    },
  },
  {
    type: "commission-insights",
    version: 1,
    label: "Analisi provvigioni",
    description: "Confronto per venditore e stato economico.",
    category: "Insight",
    accepts: ["collection"],
    defaultSpan: { desktop: 12, tablet: 8, mobile: 4 },
    defaultProps: {
      chartTitle: "Provvigioni per venditore",
      chartDescription: "Importi maturati nel periodo; apri la vista venditori per il dettaglio.",
      ctaLabel: "Apri venditori",
      statusTitle: "Stato economico",
      maxItems: 6,
    },
    bindingPolicy: "managed",
    bindingLabel: "Righe provvigionali",
    fields: {
      chartTitle: { type: "text", label: "Titolo grafico" },
      chartDescription: { type: "textarea", label: "Descrizione grafico" },
      ctaLabel: { type: "text", label: "Testo azione" },
      statusTitle: { type: "text", label: "Titolo stato economico" },
      maxItems: { type: "number", label: "Venditori visibili", min: 1, max: 20 },
    },
  },
  {
    type: "commission-workspace",
    version: 1,
    label: "Controllo provvigioni",
    description: "Filtri, raggruppamenti, modifiche, Excel ed email.",
    category: "Dati",
    accepts: ["collection"],
    defaultSpan: { desktop: 12, tablet: 8, mobile: 4 },
    defaultProps: {
      searchPlaceholder: "Cerca fattura, ordine, cliente o articolo…",
      exportLabel: "Esporta Excel",
    },
    bindingPolicy: "managed",
    bindingLabel: "Righe provvigionali",
    fields: {
      searchPlaceholder: { type: "text", label: "Testo della ricerca" },
      exportLabel: { type: "text", label: "Testo esportazione" },
    },
  },
  {
    type: "metric",
    version: 1,
    label: "Indicatore",
    description: "Un valore chiave con etichetta e contesto.",
    category: "Insight",
    accepts: ["scalar", "record"],
    defaultSpan: { desktop: 3, tablet: 4, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "required",
    fields: {},
  },
  {
    type: "status",
    version: 1,
    label: "Stato",
    description: "Esito operativo, salute o avanzamento.",
    category: "Insight",
    accepts: ["scalar", "record", "collection"],
    defaultSpan: { desktop: 3, tablet: 4, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "required",
    fields: {},
  },
  {
    type: "bar-chart",
    version: 1,
    label: "Grafico a barre",
    description: "Confronta una metrica numerica per categoria.",
    category: "Insight",
    accepts: ["collection"],
    defaultSpan: { desktop: 6, tablet: 8, mobile: 4 },
    defaultProps: { maxItems: 8 },
    bindingPolicy: "required",
    fields: { maxItems: { type: "number", label: "Categorie visibili", min: 3, max: 20 } },
  },
  {
    type: "data-table",
    version: 1,
    label: "Tabella dati",
    description: "Collezione consultabile con colonne inferite.",
    category: "Dati",
    accepts: ["collection"],
    defaultSpan: { desktop: 12, tablet: 8, mobile: 4 },
    defaultProps: { maxRows: 12 },
    bindingPolicy: "required",
    fields: { maxRows: { type: "number", label: "Righe per pagina", min: 5, max: 100 } },
  },
  {
    type: "editable-grid",
    version: 1,
    label: "Tabella modificabile",
    description: "Righe operative modificabili prima dell’approvazione.",
    category: "Dati",
    accepts: ["collection"],
    defaultSpan: { desktop: 12, tablet: 8, mobile: 4 },
    defaultProps: { maxRows: 12 },
    bindingPolicy: "required",
    fields: { maxRows: { type: "number", label: "Righe per pagina", min: 5, max: 100 } },
  },
  {
    type: "key-value",
    version: 1,
    label: "Dettagli",
    description: "Campi essenziali di un record.",
    category: "Dati",
    accepts: ["record"],
    defaultSpan: { desktop: 6, tablet: 4, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "required",
    fields: {},
  },
  {
    type: "document",
    version: 1,
    label: "Documento",
    description: "Anteprima del documento collegato al run.",
    category: "Documenti",
    accepts: ["document"],
    defaultSpan: { desktop: 6, tablet: 8, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "required",
    fields: {},
  },
  {
    type: "pdf-viewer",
    version: 1,
    label: "Anteprima PDF",
    description: "Documento PDF collegato al run o generato dal flow.",
    category: "Documenti",
    accepts: ["document"],
    defaultSpan: { desktop: 6, tablet: 8, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "required",
    fields: {},
  },
  {
    type: "cad-viewer",
    version: 1,
    label: "Disegno CAD",
    description: "Disegno tecnico con metadati e accesso all’anteprima.",
    category: "Documenti",
    accepts: ["document", "record"],
    defaultSpan: { desktop: 6, tablet: 8, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "required",
    fields: {},
  },
  {
    type: "action-bar",
    version: 1,
    label: "Azioni",
    description: "Azioni dichiarate dal flow; mai codice arbitrario.",
    category: "Azioni",
    accepts: ["action"],
    defaultSpan: { desktop: 12, tablet: 8, mobile: 4 },
    defaultProps: {},
    bindingPolicy: "none",
    fields: {},
  },
  {
    type: "approval-action",
    version: 1,
    label: "Approvazione",
    description: "Approva o rifiuta il draft attraverso l’action binding del flow.",
    category: "Azioni",
    accepts: ["action"],
    defaultSpan: { desktop: 4, tablet: 4, mobile: 4 },
    defaultProps: { label: "Approva" },
    bindingPolicy: "none",
    fields: { label: { type: "text", label: "Testo del pulsante" } },
  },
  {
    type: "writeback-action",
    version: 1,
    label: "Scrittura sul sistema",
    description: "Conferma una scrittura governata su ERP, banca o contabilità.",
    category: "Azioni",
    accepts: ["action"],
    defaultSpan: { desktop: 4, tablet: 4, mobile: 4 },
    defaultProps: { label: "Registra" },
    bindingPolicy: "none",
    fields: { label: { type: "text", label: "Testo del pulsante" } },
  },
  {
    type: "email-action",
    version: 1,
    label: "Invio email",
    description: "Invia il documento tramite l’azione email dichiarata nel flow.",
    category: "Azioni",
    accepts: ["action"],
    defaultSpan: { desktop: 4, tablet: 4, mobile: 4 },
    defaultProps: { label: "Invia email" },
    bindingPolicy: "none",
    fields: { label: { type: "text", label: "Testo del pulsante" } },
  },
] as const;

export const EXPERIENCE_COMPONENT_REGISTRY: ReadonlyMap<
  ExperienceComponentType,
  ExperienceComponentDefinition
> = new Map(definitions.map((definition) => [definition.type, definition]));

export const EXPERIENCE_COMPONENT_CATALOG = definitions;

export function createExperienceComponent(
  type: ExperienceComponentType,
  sequence: number,
  existingIds: ReadonlySet<string> = new Set(),
): ExperienceComponent {
  const definition = EXPERIENCE_COMPONENT_REGISTRY.get(type);
  if (!definition) throw new Error(`Unknown experience component: ${type}`);
  let candidate = `${type}-${sequence}`;
  let suffix = sequence;
  while (existingIds.has(candidate)) {
    suffix += 1;
    candidate = `${type}-${suffix}`;
  }
  return {
    id: candidate,
    component: type,
    componentVersion: 1,
    title: definition.label,
    span: { ...definition.defaultSpan },
    bindings: {},
    properties: { ...definition.defaultProps },
  };
}
